import React, { useEffect, useState } from 'react'
import { SampleUser } from '../../../assests/ExportImageIcon'
import service from '../../../services/service'
import endpoints from '../../../services/endpoints'
import Modal from '../../../componants/modal/Modal'
import './groupDetail.css'
import './addExpense.css'
import './expenseDetail.css'

// Wired to GET expenses/<id>/ and PATCH expense-shares/<share_id>/payment/
// — this is the real "settle a debt" action, replacing the fake Settle Up
// tab that was dropped from Group Detail (no backend for that one).
const BackArrow = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
        <path d="M15 9H4M8 5l-4 4 4 4" stroke="#1D2A4D" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
)

const BellIcon = () => (
    <svg width="15" height="15" viewBox="0 0 16 16" fill="none">
        <path
            d="M8 1.5c-2 0-3.5 1.6-3.5 3.6v2.1c0 .4-.15.8-.42 1.1L3 9.6c-.5.55-.1 1.4.63 1.4h8.74c.73 0 1.13-.85.63-1.4l-1.08-1.3a1.6 1.6 0 0 1-.42-1.1V5.1C11.5 3.1 10 1.5 8 1.5Z"
            stroke="#026D4D"
            strokeWidth="1.3"
            strokeLinejoin="round"
        />
        <path d="M6.3 13.2a1.9 1.9 0 0 0 3.4 0" stroke="#026D4D" strokeWidth="1.3" strokeLinecap="round" />
    </svg>
)

const formatDate = (value) => {
    if (!value) return ''
    const date = new Date(String(value).replace(' ', 'T'))
    if (Number.isNaN(date.getTime())) return ''
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const capitalize = (value) => (value ? String(value).charAt(0).toUpperCase() + String(value).slice(1) : '')

const formatMoney = (value) => `₹${(Number(value) || 0).toLocaleString('en-IN')}`

const ExpenseDetail = ({ expenseId, onBack }) => {
    const [expense, setExpense] = useState(null)
    const [loading, setLoading] = useState(true)
    const [payingShare, setPayingShare] = useState(null)
    const [payAmount, setPayAmount] = useState('')
    const [paySubmitting, setPaySubmitting] = useState(false)
    const [sendingReminderId, setSendingReminderId] = useState(null)

    useEffect(() => {
        if (!expenseId) { setLoading(false); return }
        setLoading(true)
        service
            .get(endpoints.splitwise.expenseDetail.replace('{expense_id}', expenseId), {}, { silent: true })
            .then(({ data }) => setExpense(data.data))
            .catch(() => setExpense(null))
            .finally(() => setLoading(false))
    }, [expenseId])

    const openMarkPaid = (share) => {
        setPayingShare(share)
        // Backend is confirmed incremental (live-tested 2026-09-15, matches
        // the v3 API doc): `paid_amount` sent here is THIS payment only —
        // the server adds it to whatever's already recorded, and auto-flips
        // to full_paid once the running total reaches the share amount.
        // Prefilling with the remaining balance means confirming as-is
        // sends exactly enough to fully settle it.
        const shareAmount = Number(share.share_amount) || 0
        const alreadyPaid = Number(share.paid_amount) || 0
        const remaining = Math.max(0, shareAmount - alreadyPaid)
        setPayAmount(String(remaining || shareAmount))
    }

    const closeMarkPaid = () => setPayingShare(null)

    // `expense.group` gives the group id directly — no need to thread it
    // in as a prop just for this. Same non-owner-gated access as the
    // Group Detail reminder buttons (see splitwise_v2_api_contract memory).
    const handleSendReminder = (memberId) => {
        if (sendingReminderId) return
        setSendingReminderId(memberId)
        service
            .post(endpoints.splitwise.paymentReminder.replace('{group_id}', expense.group), { member_id: memberId })
            .catch(() => {
                // error toast already shown by the service interceptor
            })
            .finally(() => setSendingReminderId(null))
    }

    const payAmountNum = Number(String(payAmount).replace(/[^0-9.]/g, '')) || 0
    const payShareAmount = payingShare ? Number(payingShare.share_amount) || 0 : 0
    const payAlreadyPaid = payingShare ? Number(payingShare.paid_amount) || 0 : 0
    // Projected total after this payment — for display/status only, NOT
    // what gets sent as paid_amount (the server does this addition itself).
    const payProjectedTotal = Math.min(payAlreadyPaid + payAmountNum, payShareAmount)
    const payStatus = payProjectedTotal <= 0 ? 'pending' : payProjectedTotal >= payShareAmount ? 'full_paid' : 'partial'

    const handleSubmitPayment = () => {
        if (!payingShare || paySubmitting) return
        setPaySubmitting(true)
        service
            .patch(endpoints.splitwise.expenseSharePayment.replace('{share_id}', payingShare.id), {
                payment_status: payStatus,
                paid_amount: payAmountNum.toFixed(2),
            })
            .then(({ data }) => {
                setExpense(data.data)
                closeMarkPaid()
            })
            .catch(() => {
                // error toast already shown by the service interceptor
            })
            .finally(() => setPaySubmitting(false))
    }

    if (loading) {
        return <p className='bsFareNote'>Loading expense...</p>
    }

    if (!expense) {
        return (
            <div>
                <button type='button' className='groupDetailBack' onClick={onBack}><BackArrow /></button>
                <p className='bsFareNote' style={{ marginTop: 12 }}>Expense not found.</p>
            </div>
        )
    }

    return (
        <div>
            <div className='groupDetailHead'>
                <div className='groupDetailHead__left'>
                    <button type='button' className='groupDetailBack' onClick={onBack}>
                        <BackArrow />
                    </button>
                    <div>
                        <div className='groupDetailHead__title'>{expense.title}</div>
                        <div className='groupDetailHead__subtitle'>Paid by {expense.paid_by_name} • {formatDate(expense.expense_date)}</div>
                    </div>
                </div>
            </div>

            <div className='groupDetailCard' style={{ marginBottom: 20 }}>
                <div className='balanceSummary'>
                    <div className='balanceSummary__item'>
                        <div className='balanceSummary__label'>Amount</div>
                        <div className='balanceSummary__value balanceSummary__value--spend'>{formatMoney(expense.amount)}</div>
                    </div>
                    <div className='balanceSummary__item'>
                        <div className='balanceSummary__label'>Split Type</div>
                        <div className='balanceSummary__value'>{capitalize(expense.split_type)}</div>
                    </div>
                    <div className={`balanceSummary__item ${expense.payment_status !== 'full_paid' ? 'balanceSummary__item--owe' : ''}`}>
                        <div className='balanceSummary__label'>Status</div>
                        <div className='balanceSummary__value'>{expense.payment_status === 'full_paid' ? 'Settled' : capitalize(expense.payment_status)}</div>
                    </div>
                </div>
            </div>

            <div className='groupDetailCard'>
                <div className='groupDetailCard__title' style={{ marginBottom: 8 }}>Shares</div>
                {(expense.shares || []).map((share) => (
                    <div className='expenseShareRow' key={share.id}>
                        <img src={share.member_image || SampleUser} alt="" className='groupMemberRow__avatar' />
                        <div className='expenseShareRow__info'>
                            <div className='expenseShareRow__name'>
                                {share.member_name}
                                {share.is_payer && <span className='expenseShareRow__payerTag'>Paid the bill</span>}
                            </div>
                            {share.percentage != null && (
                                <div className='expenseShareRow__note'>{share.percentage}%</div>
                            )}
                            {!share.is_payer && share.payment_status === 'partial' && (
                                <div className='expenseShareRow__note'>{formatMoney(share.paid_amount)} paid of {formatMoney(share.share_amount)}</div>
                            )}
                        </div>
                        <div className='expenseShareRow__amountCol'>
                            <div className='expenseShareRow__amount'>
                                {!share.is_payer && share.payment_status === 'partial'
                                    ? formatMoney(share.pending_amount)
                                    : formatMoney(share.share_amount)}
                            </div>
                            {!share.is_payer && share.payment_status === 'partial' && (
                                <div className='expenseShareRow__amountLabel'>remaining</div>
                            )}
                        </div>
                        {share.is_payer ? (
                            <span className='expenseShareRow__status expenseShareRow__status--settled'>✓ Paid</span>
                        ) : share.payment_status === 'full_paid' ? (
                            <span className='expenseShareRow__status expenseShareRow__status--settled'>✓ Settled</span>
                        ) : (
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <button
                                    type='button'
                                    className='groupMemberRow__reminderBtn'
                                    disabled={sendingReminderId === share.member}
                                    onClick={() => handleSendReminder(share.member)}
                                    title='Send payment reminder'
                                >
                                    <BellIcon />
                                </button>
                                <button
                                    type='button'
                                    className='settleDebtRow__settleBtn'
                                    onClick={() => openMarkPaid(share)}
                                >
                                    Mark as Paid
                                </button>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <Modal open={Boolean(payingShare)} onClose={closeMarkPaid} maxWidth={400}>
                {payingShare && (
                    <>
                        <div className='groupDetailCard__title' style={{ marginBottom: 4 }}>Mark Payment</div>
                        <p className='bsFareNote' style={{ marginBottom: 18 }}>
                            {payingShare.member_name}'s share is {formatMoney(payingShare.share_amount)}
                            {payAlreadyPaid > 0 ? ` — ${formatMoney(payAlreadyPaid)} already paid, ${formatMoney(payShareAmount - payAlreadyPaid)} remaining.` : '.'}
                        </p>
                        <div className='addExpensePage__field' style={{ marginBottom: 8 }}>
                            <label>Amount Paying Now</label>
                            <div className='addExpensePage__amountInput'>
                                <span>₹</span>
                                <input type='text' value={payAmount} onChange={(e) => setPayAmount(e.target.value)} />
                            </div>
                        </div>
                        <p className='bsFareNote' style={{ marginBottom: 18 }}>
                            Total paid so far will become <strong>{formatMoney(payProjectedTotal)}</strong> of {formatMoney(payShareAmount)}
                            {payStatus === 'partial' ? <> (<strong>{formatMoney(payShareAmount - payProjectedTotal)}</strong> will still remain)</> : ''} — marked as{' '}
                            <strong>{payStatus === 'full_paid' ? 'Fully Paid' : payStatus === 'partial' ? 'Partially Paid' : 'Pending'}</strong>.
                        </p>
                        <div className='addExpensePage__actions'>
                            <button type='button' className='addExpensePage__cancelBtn' onClick={closeMarkPaid}>Cancel</button>
                            <button type='button' className='addExpensePage__addBtn' disabled={paySubmitting} onClick={handleSubmitPayment}>
                                {paySubmitting ? 'Saving...' : 'Save'}
                            </button>
                        </div>
                    </>
                )}
            </Modal>
        </div>
    )
}

export default ExpenseDetail
